import { MutableRefObject, useCallback } from "react";
import findIndex from "../helpers/findIndex";
import { KeyExtractor, KeyLookup, PositionLookup } from "../types";

const useFillPositionLookup = <TItem>({
	curIndex,
	dataRef,
	keyExtractor,
	curKey,
}: {
	dataRef: MutableRefObject<TItem[]>;
	curIndex: MutableRefObject<number>;
	keyExtractor: KeyExtractor<TItem>;
	curKey: MutableRefObject<string>;
}) => {
	/**
	 * Used to populate the position lookup
	 *
	 * Pretty expensive to run since it traverses the entire data array
	 */
	const fillPositionLookup = useCallback(
		(curPositionLookup: PositionLookup, populateKeyLookup: KeyLookup) => {
			curIndex.current = findIndex({
				data: dataRef.current,
				keyExtractor,
				targetKey: curKey.current,
				prevIndex: curIndex.current,
			});
			if (!curPositionLookup[curKey.current]) {
				curPositionLookup[curKey.current] = {
					positionX: 0,
					dataIndex: curIndex.current,
				};
				populateKeyLookup[0] = curKey.current;
			}
			const initialLookup = curPositionLookup[curKey.current];
			if (!initialLookup) throw new Error("initialLookup is undefined");

			for (
				let i = curIndex.current + 1, curPx = initialLookup.positionX + 1;
				i < dataRef.current.length;
				i++, curPx++
			) {
				const cur = dataRef.current[i];
				if (!cur) throw new Error("cur is undefined");

				const curKey = keyExtractor({ item: cur });
				curPositionLookup[curKey] = {
					positionX: curPx,
					dataIndex: i,
				};
				populateKeyLookup[curPx] = curKey;
			}
			for (
				let i = curIndex.current - 1, curPx = initialLookup.positionX - 1;
				i >= 0;
				i--, curPx--
			) {
				const cur = dataRef.current[i];
				if (!cur) throw new Error("cur is undefined");

				const curKey = keyExtractor({ item: cur });
				curPositionLookup[curKey] = {
					positionX: curPx,
					dataIndex: i,
				};
				populateKeyLookup[curPx] = curKey;
			}
			return curPositionLookup;
		},
		[keyExtractor, curIndex, curKey, dataRef],
	);
	return fillPositionLookup;
};
export default useFillPositionLookup;
